import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * §9.1's "impossible-to-miss" impersonation banner, landing side. `platform-admin`
 * renders its own copy of this for as long as ITS `ImpersonationContext` session is
 * active — that's the operator's view. This is the other half: for as long as the
 * session actually sitting in *this* app's `AuthContext` is an impersonation session
 * (`isImpersonating`, i.e. `session.refreshToken === null`), the tenant-realm app the
 * operator is actually doing support work in stays visibly marked too, exactly the
 * wording `system_architecture.md` §9.1 specifies. Rendered at the top of every route
 * (see `App.tsx`), matching `platform-admin/src/components/ImpersonationBanner.tsx`'s
 * styling so the two are recognizably the same feature.
 */
export function ImpersonationBanner() {
  const { session, isImpersonating, logout } = useAuth();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isImpersonating) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isImpersonating]);

  if (!isImpersonating || !session) return null;

  const remainingMs = Math.max(0, new Date(session.expiresAt).getTime() - now);

  return (
    <div className="impersonation-banner" role="alert">
      <span className="impersonation-banner__label">IMPERSONATING</span>
      <span className="impersonation-banner__text">
        A platform operator is viewing this account as <strong>{session.user.email}</strong> — this session is
        logged and expires in <strong>{formatCountdown(remainingMs)}</strong>.
      </span>
      <button
        type="button"
        className="impersonation-banner__end"
        onClick={() => void logout()}
        title="Ends this impersonation session in this tab. The token itself is short-lived and non-refreshable by design — there is no separate server-side revoke endpoint to call."
      >
        End session
      </button>
    </div>
  );
}
